import type {
  CaptchaChallenge,
  CaptchaRequest,
  ChangePasswordInput,
  ChangePasswordResult,
  DailyBonusClaimResult,
  DailyBonusState,
  DeletionRequestInput,
  DeletionResult,
  GameSummary,
  IdentitiesResult,
  JackpotDrawResponse,
  JackpotResponse,
  LimitsInput,
  LimitsState,
  LobbyLiveResponse,
  LoginInput,
  LoginResult,
  LogoutAllResult,
  Me,
  PassportResponse,
  ProvidersResult,
  RegisterInput,
  ResendVerificationResult,
  SeasonResponse,
  SeedInfo,
  SetEmailInput,
  SetPasswordInput,
  SetPasswordResult,
  SettingsInput,
  TelegramAuthInput,
  TelegramAuthResult,
  VerifyEmailResult,
  AuthProviderId,
} from '@tg/shared';
import { deviceSignals } from './device';
import { api, type RequestOptions } from './client';

/**
 * Typed REST endpoints of apps/server (contracts in @tg/shared).
 * Account endpoints answer `{ me }`.
 */
type MeRes = { me: Me };

export const endpoints = {
  me: async () => (await api.get<MeRes>('/api/me')).me,
  /** `deletionCancelled`: the sign-in cancelled a pending account deletion */
  login: (b: LoginInput) => api.post<LoginResult>('/api/auth/login', b),
  /** `device`: signals for the invite-bonus check (hashed on the server, see api/device.ts) */
  register: async (b: RegisterInput) => (await api.post<MeRes>('/api/auth/register', { ...b, device: deviceSignals() })).me,
  logout: () => api.post<{ ok: true }>('/api/auth/logout'),
  /** ends every session of the account (all devices, this one too) */
  logoutAll: () => api.post<LogoutAllResult>('/api/auth/logout-all'),
  /** checks the current password; every other session of the account is signed out */
  changePassword: (b: ChangePasswordInput) => api.post<ChangePasswordResult>('/api/auth/password', b),
  /** an account without a password sets one (no current password; the session must be fresh, else REAUTH_REQUIRED) */
  setPassword: async (b: SetPasswordInput) => (await api.post<SetPasswordResult>('/api/auth/password/set', b)).me,
  /** proof-of-work challenge (solved in a Web Worker: features/auth/captcha) */
  captcha: (b: CaptchaRequest) => api.post<CaptchaChallenge>('/api/auth/captcha', b),
  /** the token from the confirmation link; no session needed */
  verifyEmail: (token: string) => api.post<VerifyEmailResult>('/api/auth/verify-email', { token }),
  resendVerification: () => api.post<ResendVerificationResult>('/api/auth/verify-email/resend'),
  /** sign-in providers configured on the server (Google / Apple / Telegram); Google and Apple are page navigations */
  providers: () => api.get<ProvidersResult>('/api/auth/providers'),
  /** Telegram Login Widget data → session (`mode: 'login'`), a link to the signed-in account (`'link'`) or a confirmation of it (`'reauth'`) */
  telegramAuth: (b: TelegramAuthInput) =>
    api.post<TelegramAuthResult | IdentitiesResult>('/api/auth/oauth/telegram', b.mode === 'link' || b.mode === 'reauth' ? b : { ...b, device: deviceSignals() }),
  identities: () => api.get<IdentitiesResult>('/api/me/identities'),
  unlinkIdentity: (p: AuthProviderId) => api.del<IdentitiesResult>(`/api/me/identities/${p}`),
  /** an account without an e-mail (Telegram sign-up) adds one; the confirmation letter goes out */
  setEmail: async (b: SetEmailInput) => (await api.post<MeRes>('/api/me/email', b)).me,
  settings: async (b: SettingsInput) => (await api.patch<MeRes>('/api/me/settings', b)).me,
  /** account deletion (7 days to cancel): password + own nickname; `captcha` after CAPTCHA_REQUIRED */
  requestDeletion: async (b: DeletionRequestInput) => (await api.post<DeletionResult>('/api/me/deletion', b)).me,
  cancelDeletion: async () => (await api.post<DeletionResult>('/api/me/deletion/cancel')).me,
  /** "my data" (GDPR access): everything the platform keeps about the player, as one JSON document */
  exportMyData: () => api.get<Record<string, unknown>>('/api/me/export'),

  dailyBonus: () => api.get<DailyBonusState>('/api/bonus/daily'),
  claimDailyBonus: () => api.post<DailyBonusClaimResult>('/api/bonus/daily/claim'),

  games: async (o?: RequestOptions) => (await api.get<{ games: GameSummary[] }>('/api/games', o)).games,
  lobbyLive: (o?: RequestOptions) => api.get<LobbyLiveResponse>('/api/lobby/live', { ...o, cache: 'no-store' }),
  season: (o?: RequestOptions) => api.get<SeasonResponse>('/api/lobby/season', o),
  jackpot: (o?: RequestOptions) => api.get<JackpotResponse>('/api/jackpot', o),
  jackpotDraw: (betId: string) => api.get<JackpotDrawResponse>(`/api/jackpot/draws/${encodeURIComponent(betId)}`),

  /** Game Passport: stamps, catalog size, rewards (the server credits a reward the history already reached) */
  passport: (o?: RequestOptions) => api.get<PassportResponse>('/api/me/passport', o),

  limits: () => api.get<LimitsState>('/api/limits'),
  setLimits: (b: LimitsInput) => api.put<LimitsState>('/api/limits', b),
  takeBreak: (period: '24h' | '7d' | '30d') => api.post<LimitsState>('/api/limits/break', { period }),

  seeds: () => api.get<SeedInfo>('/api/fair/seeds'),
  setClientSeed: (clientSeed: string) => api.post<SeedInfo>('/api/fair/client-seed', { clientSeed }),
  rotateSeed: () => api.post<SeedInfo>('/api/fair/rotate'),
};

/** Lobby ticker item (from `LobbyLiveResponse.wins`). */
export interface TickerItem {
  id: string;
  nickname: string;
  /** payout in tokens */
  amount: number;
  game: GameSummary['id'];
  multiplier?: number | null;
  color?: 'red' | 'black' | 'green' | null;
}
